import { createElement, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppSelector } from './redux';
import { onForegroundMessage } from '@/lib/firebase';
import { autoRequestPushPermission } from '@/lib/pushNotifications';
import { getNotificationUrl } from '@/lib/notificationLink';
import { PushNotificationToast } from '@/components/common/PushNotificationToast';

/**
 * Once a user is logged in, asks for notification permission (if not answered
 * yet) and registers the FCM token with the backend (the token needs to be tied
 * to a user). PushNotificationPrompt is the fallback when the browser suppresses
 * that automatic prompt.
 * Also shows foreground push messages as in-app toasts (clickable when they
 * link to a page) — background messages are shown as OS notifications by
 * public/sw.js instead.
 */
export function useFcmNotifications() {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const navigate = useNavigate();
  // useNavigate's identity changes on every route change; read it via a ref so
  // the listeners below subscribe once instead of on every navigation.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  useEffect(() => {
    if (isAuthenticated) autoRequestPushPermission();
  }, [isAuthenticated]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let unmounted = false;

    onForegroundMessage(({ messageId, title, body, data }) => {
      const url = getNotificationUrl(data);
      const subtitle = [data?.sport, data?.competition].filter(Boolean).join(' • ');

      toast(
        ({ closeToast }) =>
          createElement(PushNotificationToast, {
            title: title || 'New notification',
            body,
            subtitle,
            onClick: url ? () => navigateRef.current(url) : undefined,
            closeToast,
          }),
        {
          toastId: messageId,
          autoClose: 6000,
          closeButton: false,
          icon: false,
          // Wider than regular toasts; on phones (<=480px) react-toastify makes it full width.
          className: '!min-h-0 !bg-transparent !p-0 !shadow-none min-[481px]:!w-[400px]',
        }
      );
    }).then((unsub) => {
      if (unmounted) unsub();
      else unsubscribe = unsub;
    });

    return () => {
      unmounted = true;
      unsubscribe?.();
    };
  }, []);

  // Clicks on background (OS) notifications are relayed by public/sw.js when a
  // tab is already open, so route in-app instead of reloading the page.
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && typeof event.data.url === 'string') {
        console.log('[FCM] notification click, navigating to', event.data.url);
        navigateRef.current(event.data.url);
      }
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    // Messages are queued until startMessages() when using addEventListener.
    navigator.serviceWorker.startMessages();

    return () => navigator.serviceWorker.removeEventListener('message', handleMessage);
  }, []);
}
